
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWeb3 } from '../../hooks/useWeb3';
import { useProjects } from '../../hooks/useProjects';
import { useContract } from '../../hooks/useContract';
import { Project } from '../../services/api';
import { api } from '../../services/api';
import { ethers } from 'ethers';
import { formatAddress, formatEther, parseEther } from '../../services/web3';
import { CONTRACT_ADDRESS, ESCROW_ABI } from '../../services/contracts/config';
import { getTransactionUrl, getContractUrl } from '../../services/blockchain/explorer';
import { getProjectEvents } from '../../services/blockchain/events';
import { getProjectTransactions } from '../../services/blockchain/polygonscan';
import MilestoneCard from '../../components/project/MilestoneCard';
import TransactionHistory, { Transaction } from '../../components/project/TransactionHistory';
import DashboardHeader from '../../components/common/DashboardHeader';
import Toast from '../../components/common/Toast';
import BlockchainLoader from '../../components/common/BlockchainLoader';
import DisputeModal from '../../components/project/DisputeModal';
import DisputeResolutionModal from '../../components/project/DisputeResolutionModal';
import EvidenceSubmissionModal from '../../components/project/EvidenceSubmissionModal';

const ProjectDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { account, signer, provider } = useWeb3();
  const {
    fetchProject,
    approveMilestone: approveMilestoneAPI,
    releaseMilestone: releaseMilestoneAPI,
    openDispute: openDisputeAPI,
    setArbitrator: setArbitratorAPI,
    resolveDispute: resolveDisputeAPI,
    submitEvidence: submitEvidenceAPI,
    getEvidence,
  } = useProjects();
  const {
    approveMilestone: approveMilestoneContract,
    releasePayment: releasePaymentContract,
    depositFunds,
    getProject: getProjectFromContract,
    openDispute: openDisputeContract,
    setArbitrator: setArbitratorContract,
    resolveDispute: resolveDisputeContract,
  } = useContract(signer);

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [blockchainLoading, setBlockchainLoading] = useState(false);
  const [blockchainLoadingMessage, setBlockchainLoadingMessage] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(
    null,
  );
  const isFullyFunded = project ? BigInt(project.depositedAmount) >= BigInt(project.totalAmount) : false;
  const [depositDocument, setDepositDocument] = useState<File | null>(null);
  const [depositComment, setDepositComment] = useState('');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [projectDocuments, setProjectDocuments] = useState<any[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [milestoneDocuments, setMilestoneDocuments] = useState<{ [key: number]: any[] }>({});
  const [uploadingMilestone, setUploadingMilestone] = useState<number | null>(null);
  const [milestoneUploadFiles, setMilestoneUploadFiles] = useState<{ [key: number]: File | null }>({});
  const [milestoneUploadComments, setMilestoneUploadComments] = useState<{ [key: number]: string }>({});
  const [disputeModalOpen, setDisputeModalOpen] = useState(false);
  const [disputingMilestone, setDisputingMilestone] = useState<number | null>(null);
  const [arbitratorAddress, setArbitratorAddress] = useState('');
  const [resolveModalOpen, setResolveModalOpen] = useState(false);
  const [resolvingMilestone, setResolvingMilestone] = useState<number | null>(null);
  const [evidenceModalOpen, setEvidenceModalOpen] = useState(false);
  const [evidenceMilestone, setEvidenceMilestone] = useState<number | null>(null);
  const [milestoneEvidence, setMilestoneEvidence] = useState<{ [key: number]: any[] }>({});
  const [milestoneDisputeReasons, setMilestoneDisputeReasons] = useState<{ [key: number]: { reason: string; initiator: string } }>({});

  // Load project documents
  const loadProjectDocuments = async () => {
    if (!id) return;
    setDocumentsLoading(true);
    try {
      // Load project-level documents (milestoneIndex = null)
      const projectDocs = await api.getDocuments(id);
      setProjectDocuments(projectDocs.filter((doc: any) => doc.milestone_index === null || doc.milestone_index === undefined));
      
      // Load milestone documents
      if (project && project.milestones) {
        const milestoneDocs: { [key: number]: any[] } = {};
        for (let i = 0; i < project.milestones.length; i++) {
          try {
            const docs = await api.getDocuments(id, i);
            milestoneDocs[i] = docs;
          } catch (e) {
            milestoneDocs[i] = [];
          }
        }
        setMilestoneDocuments(milestoneDocs);
      }
    } catch (e: any) {
      console.warn('Failed to load project documents:', e);
      setProjectDocuments([]);
    } finally {
      setDocumentsLoading(false);
    }
  };

  useEffect(() => {
    if (!id) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchProject(id);
        setProject(data);
        
        // Load project documents
        await loadProjectDocuments();
        
        // Load transactions from database first (filtered by project ID)
        try {
          const dbTransactions = await api.getTransactions(id);
          console.log('Loaded transactions from database for project', id, ':', dbTransactions.length);
          // Double-check: filter to ensure all transactions belong to this project
          // Backend already filters by projectId, but we verify here too
          const filteredTransactions = dbTransactions.filter(tx => {
            // Transactions should already be filtered by backend
            // But we can add additional validation if needed
            return true; // Backend query already filters by project_id
          });
          if (filteredTransactions.length > 0) {
            setTransactions(filteredTransactions);
            console.log('Set', filteredTransactions.length, 'transactions for project', id);
          } else {
            // If no transactions in DB, initialize with empty array
            setTransactions([]);
            console.log('No transactions found for project', id);
          }
          
          // Add create transaction if it doesn't exist and project is loaded
          if (project) {
            setTransactions((prev) => {
              const hasCreateTransaction = prev.some(tx => tx.type === 'create');
              if (!hasCreateTransaction) {
                const createTransaction: Transaction = {
                  id: `create-${project.id}`,
                  type: 'create',
                  from: project.client,
                  to: project.provider,
                  timestamp: project.createdAt,
                };
                const updated = [...prev, createTransaction];
                // Sort transactions by timestamp (newest first, but create should be at the end)
                updated.sort((a, b) => {
                  const timeA = new Date(a.timestamp).getTime();
                  const timeB = new Date(b.timestamp).getTime();
                  // If one is create, put it at the end
                  if (a.type === 'create' && b.type !== 'create') return 1;
                  if (b.type === 'create' && a.type !== 'create') return -1;
                  // Otherwise sort by newest first
                  return timeB - timeA;
                });
                return updated;
              }
              return prev;
            });
          }
        } catch (e: any) {
          console.warn('Failed to load transactions from database:', e.message);
          setTransactions([]);
        }
      } catch (e: any) {
        setError(e.message || 'Failed to load project');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, fetchProject]);

  // Sync blockchain events via server-side endpoint (non-blocking, runs after project is loaded)
  useEffect(() => {
    if (!project) return;

    const projectBlockchainId = project && (project as any).blockchainProjectId != null
      ? Number((project as any).blockchainProjectId)
      : null;

    if (!projectBlockchainId || projectBlockchainId <= 0) return;

    const syncFromBlockchain = async () => {
      try {
        console.log('Syncing blockchain data via server for project:', project.id);
        const result = await api.syncBlockchain(project.id);
        console.log('Server blockchain sync result:', result.message, '| synced:', result.synced);

        if (result.transactions && result.transactions.length > 0) {
          // Add milestone descriptions
          const enriched = result.transactions.map((tx: any) => {
            if ((tx.type === 'approve' || tx.type === 'release') && tx.milestoneIndex !== undefined) {
              const milestone = project.milestones[tx.milestoneIndex];
              if (milestone) {
                return { ...tx, milestoneDescription: milestone.description, amount: tx.amount || milestone.amount };
              }
            }
            return tx;
          });

          // Add create transaction if missing
          if (!enriched.some((tx: any) => tx.type === 'create')) {
            enriched.push({
              id: `create-${project.id}`,
              type: 'create',
              from: project.client,
              to: project.provider,
              timestamp: project.createdAt,
            });
          }

          // Sort: newest first, create at end
          enriched.sort((a: any, b: any) => {
            if (a.type === 'create' && b.type !== 'create') return 1;
            if (b.type === 'create' && a.type !== 'create') return -1;
            return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
          });

          setTransactions(enriched);
        }

        // Reload project if deposited amount changed
        if (result.synced > 0) {
          const updatedData = await fetchProject(project.id);
          setProject(updatedData);
        }
      } catch (error: any) {
        console.warn('Server blockchain sync failed (non-critical):', error.message);
      }
    };

    const timeoutId = setTimeout(syncFromBlockchain, 500);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  const isClient =
    project && account ? project.client.toLowerCase() === account.toLowerCase() : false;
  const isProvider =
    project && account ? project.provider.toLowerCase() === account.toLowerCase() : false;
  const isArbitrator =
    project && account && (project as any).arbitratorAddress
      ? (project as any).arbitratorAddress.toLowerCase() === account.toLowerCase()
      : false;

  const total = project ? Number(formatEther(project.totalAmount)) : 0;
  const rawDeposited = project ? Number(formatEther(project.depositedAmount)) : 0;
  // On completed projects, contract decrements depositedAmount to 0 after releasing funds
  // Show the total as deposited since all funds were processed
  const deposited = project?.status === 'completed' ? total : rawDeposited;
  const remaining = project?.status === 'completed' ? 0 : Math.max(0, total - rawDeposited);
  const hasDeposit = deposited > 0;
  const hasDocument = projectDocuments.length > 0;

  const releasedTotal =
    project?.milestones
      .filter((m) => m.released)
      .reduce((acc, m) => acc + Number(formatEther(m.amount)), 0) ?? 0;

  const progress =
    project && total > 0 ? Math.min(100, Math.round((releasedTotal / total) * 100)) : 0;

  const blockchainProjectId =
    project && (project as any).blockchainProjectId != null
      ? Number((project as any).blockchainProjectId)
      : null;

  // Debug logging
  useEffect(() => {
    if (project) {
      console.log('Project loaded:', {
        id: project.id,
        blockchainProjectId: (project as any).blockchainProjectId,
        parsedBlockchainProjectId: blockchainProjectId,
        hasSigner: !!signer,
        account,
      });
    }
  }, [project, blockchainProjectId, signer, account]);

  const reloadProject = async () => {
    if (!project) return;
    try {
      const data = await fetchProject(project.id);
      setProject(data);
      
      // Blockchain transactions will be reloaded automatically by the useEffect
      // that watches project.id, so we don't need to reload them here
    } catch (e: any) {
      setError(e.message || 'Failed to reload project');
    }
  };

  const handleApprove = async (milestoneIndex: number) => {
    if (!project || !account) {
      setToast({ message: 'Please connect your wallet', type: 'error' });
      return;
    }

    setActionLoading(true);
    setBlockchainLoading(true);
    setBlockchainLoadingMessage('Approving milestone on blockchain...');
    setError(null);

    try {
      const asClient = isClient;

      let txHash: string | undefined;

      // On-chain (required if blockchainProjectId exists)
      if (signer && blockchainProjectId != null && blockchainProjectId >= 0) {
        try {
          console.log('Approving milestone on-chain:', { blockchainProjectId, milestoneIndex });
          const receipt = await approveMilestoneContract(blockchainProjectId, milestoneIndex);
          txHash = receipt.hash;
          console.log('On-chain approve successful, txHash:', txHash);
        } catch (e: any) {
          console.error('On-chain approve failed:', e);
          const errorMsg = e.message || e.reason || 'Blockchain transaction failed';
          throw new Error(`Blockchain approval failed: ${errorMsg}. Please try again.`);
        }
      } else if (blockchainProjectId == null) {
        console.warn('No blockchainProjectId found, skipping on-chain approval');
      }

      setBlockchainLoadingMessage('Updating project data...');
      // Off-chain
      await approveMilestoneAPI(project.id, milestoneIndex, account, asClient);
      
      // Add transaction to history (if not already loaded from blockchain)
      if (txHash && account) {
        const milestone = project.milestones[milestoneIndex];
        setTransactions((prev) => {
          // Check if transaction already exists
          const exists = prev.some((tx) => tx.txHash === txHash);
          if (exists) return prev;
          
          return [
            {
              id: `approve-${Date.now()}`,
              type: 'approve',
              txHash,
              milestoneIndex,
              milestoneDescription: milestone.description,
              from: account,
              timestamp: new Date().toISOString(),
            },
            ...prev,
          ];
        });
      }
      
      setToast({ message: 'Milestone approved successfully', type: 'success' });
      setBlockchainLoading(false);
      setBlockchainLoadingMessage('');
      setActionLoading(false);
      reloadProject();
    } catch (e: any) {
      const errorMsg = e.message || 'Failed to approve milestone';
      setError(errorMsg);
      setToast({ message: errorMsg, type: 'error' });
      setBlockchainLoading(false);
      setBlockchainLoadingMessage('');
      setActionLoading(false);
    }
  };

  const handleRelease = async (milestoneIndex: number) => {
    if (!project || !account) {
      setToast({ message: 'Please connect your wallet', type: 'error' });
      return;
    }

    setActionLoading(true);
    setBlockchainLoading(true);
    setBlockchainLoadingMessage('Releasing payment on blockchain...');
    setError(null);

    try {
      let txHash: string | undefined;

      // On-chain (required if blockchainProjectId exists)
      if (signer && blockchainProjectId != null && blockchainProjectId >= 0) {
        try {
          console.log('Releasing payment on-chain:', { blockchainProjectId, milestoneIndex });
          const receipt = await releasePaymentContract(blockchainProjectId, milestoneIndex);
          txHash = receipt.hash;
          console.log('On-chain release successful, txHash:', txHash);
        } catch (e: any) {
          console.error('On-chain release failed:', e);
          const errorMsg = e.message || e.reason || 'Blockchain transaction failed';
          throw new Error(`Blockchain release failed: ${errorMsg}. Please try again.`);
        }
      } else if (blockchainProjectId == null) {
        console.warn('No blockchainProjectId found, skipping on-chain release');
      }

      setBlockchainLoadingMessage('Updating project data...');
      // Off-chain
      const releaseResponse = await releaseMilestoneAPI(project.id, milestoneIndex);
      
      // Check if project was completed
      const isCompleted = releaseResponse?.message?.includes('completed') || 
                         releaseResponse?.project?.status === 'completed';
      
      // Add transaction to history (if not already loaded from blockchain)
      if (txHash && account) {
        const milestone = project.milestones[milestoneIndex];
        setTransactions((prev) => {
          // Check if transaction already exists
          const exists = prev.some((tx) => tx.txHash === txHash);
          if (exists) return prev;
          
          return [
            {
              id: `release-${Date.now()}`,
              type: 'release',
              txHash,
              amount: milestone.amount,
              milestoneIndex,
              milestoneDescription: milestone.description,
              from: account,
              timestamp: new Date().toISOString(),
            },
            ...prev,
          ];
        });
      }
      
      // Show appropriate message
      if (isCompleted) {
        setToast({ message: 'Payment released. Project completed!', type: 'success' });
      } else {
        setToast({ message: 'Payment released', type: 'success' });
      }

      setBlockchainLoading(false);
      setBlockchainLoadingMessage('');
      setActionLoading(false);
      reloadProject();
    } catch (e: any) {
      setError(e.message || 'Failed to release payment');
      setToast({ message: e.message || 'Failed to release payment', type: 'error' });
      setBlockchainLoading(false);
      setBlockchainLoadingMessage('');
      setActionLoading(false);
    }
  };

  const handleOpenDisputeModal = (milestoneIndex: number) => {
    setDisputingMilestone(milestoneIndex);
    setDisputeModalOpen(true);
  };

  const handleOpenDispute = async (reason: string) => {
    if (!project || !account || disputingMilestone === null) {
      setToast({ message: 'Please connect your wallet', type: 'error' });
      return;
    }

    setDisputeModalOpen(false);
    setActionLoading(true);
    setBlockchainLoading(true);
    setBlockchainLoadingMessage('Opening dispute on blockchain...');
    setError(null);

    try {
      let txHash: string | undefined;

      // On-chain (required if blockchainProjectId exists)
      if (signer && blockchainProjectId != null && blockchainProjectId >= 0) {
        try {
          console.log('Opening dispute on-chain:', { blockchainProjectId, disputingMilestone, reason });
          const receipt = await openDisputeContract(blockchainProjectId, disputingMilestone, reason);
          txHash = receipt.hash;
          console.log('On-chain dispute open successful, txHash:', txHash);
        } catch (e: any) {
          console.error('On-chain dispute open failed:', e);
          const errorMsg = e.message || e.reason || 'Blockchain transaction failed';
          throw new Error(`Blockchain dispute open failed: ${errorMsg}. Please try again.`);
        }
      } else if (blockchainProjectId == null) {
        throw new Error('No blockchain project ID found. Cannot open dispute.');
      }

      if (!txHash) {
        throw new Error('Transaction hash not received from blockchain');
      }

      setBlockchainLoadingMessage('Recording dispute data...');
      // Off-chain
      await openDisputeAPI(project.id, disputingMilestone, reason, txHash, account);

      // Add transaction to history
      if (txHash && account) {
        const milestone = project.milestones[disputingMilestone];
        setTransactions((prev) => {
          // Check if transaction already exists
          const exists = prev.some((tx) => tx.txHash === txHash);
          if (exists) return prev;

          return [
            {
              id: `dispute-${Date.now()}`,
              type: 'dispute',
              txHash,
              milestoneIndex: disputingMilestone,
              milestoneDescription: milestone.description,
              from: account,
              timestamp: new Date().toISOString(),
            },
            ...prev,
          ];
        });
      }

      setToast({ message: 'Dispute opened successfully', type: 'success' });
      setDisputeModalOpen(false);
      setDisputingMilestone(null);
      setBlockchainLoading(false);
      setBlockchainLoadingMessage('');
      setActionLoading(false);
      reloadProject();
    } catch (e: any) {
      const errorMsg = e.message || 'Failed to open dispute';
      setError(errorMsg);
      setToast({ message: errorMsg, type: 'error' });
      setBlockchainLoading(false);
      setBlockchainLoadingMessage('');
      setActionLoading(false);
    }
  };

  const handleSetArbitrator = async () => {
    if (!project || !account) {
      setToast({ message: 'Please connect your wallet', type: 'error' });
      return;
    }

    // Validate address format
    if (!ethers.isAddress(arbitratorAddress)) {
      setToast({ message: 'Invalid Ethereum address format', type: 'error' });
      return;
    }

    // Prevent assigning client or provider as arbitrator
    if (project.provider.toLowerCase() === arbitratorAddress.toLowerCase()) {
      setToast({ message: 'Provider cannot be the arbitrator', type: 'error' });
      return;
    }

    // Block if client is assigning themselves
    if (arbitratorAddress.toLowerCase() === account.toLowerCase()) {
      setToast({ message: 'You cannot assign yourself as arbitrator', type: 'error' });
      return;
    }

    setActionLoading(true);
    setBlockchainLoading(true);
    setBlockchainLoadingMessage('Assigning arbitrator on blockchain...');
    setError(null);

    try {
      let txHash: string | undefined;

      // On-chain (required if blockchainProjectId exists)
      if (signer && blockchainProjectId != null && blockchainProjectId >= 0) {
        try {
          console.log('Assigning arbitrator on-chain:', { blockchainProjectId, arbitratorAddress });
          const receipt = await setArbitratorContract(blockchainProjectId, arbitratorAddress);
          txHash = receipt.hash;
          console.log('On-chain arbitrator assignment successful, txHash:', txHash);
        } catch (e: any) {
          console.error('On-chain arbitrator assignment failed:', e);
          const errorMsg = e.message || e.reason || 'Blockchain transaction failed';
          throw new Error(`Blockchain arbitrator assignment failed: ${errorMsg}. Please try again.`);
        }
      } else if (blockchainProjectId == null) {
        throw new Error('No blockchain project ID found. Cannot assign arbitrator.');
      }

      if (!txHash) {
        throw new Error('Transaction hash not received from blockchain');
      }

      setBlockchainLoadingMessage('Recording arbitrator data...');
      // Off-chain
      await setArbitratorAPI(project.id, arbitratorAddress, txHash);

      // Add transaction to history
      if (txHash && account) {
        setTransactions((prev) => {
          // Check if transaction already exists
          const exists = prev.some((tx) => tx.txHash === txHash);
          if (exists) return prev;

          return [
            {
              id: `arbitrator-${Date.now()}`,
              type: 'arbitrator',
              txHash,
              from: account,
              to: arbitratorAddress,
              timestamp: new Date().toISOString(),
            },
            ...prev,
          ];
        });
      }

      setToast({ message: 'Arbitrator assigned successfully', type: 'success' });
      setArbitratorAddress('');
      setBlockchainLoading(false);
      setBlockchainLoadingMessage('');
      setActionLoading(false);
      reloadProject();
    } catch (e: any) {
      const errorMsg = e.message || 'Failed to assign arbitrator';
      setError(errorMsg);
      setToast({ message: errorMsg, type: 'error' });
      setBlockchainLoading(false);
      setBlockchainLoadingMessage('');
      setActionLoading(false);
    }
  };

  const handleOpenResolveModal = (milestoneIndex: number) => {
    setResolvingMilestone(milestoneIndex);
    setResolveModalOpen(true);
  };

  const handleOpenEvidenceModal = (milestoneIndex: number) => {
    setEvidenceMilestone(milestoneIndex);
    setEvidenceModalOpen(true);
  };

  const loadEvidenceForMilestone = async (milestoneIndex: number) => {
    if (!project) return;
    try {
      const evidence = await getEvidence(project.id, milestoneIndex);
      setMilestoneEvidence(prev => ({ ...prev, [milestoneIndex]: evidence }));
    } catch (e) {
      console.warn('Failed to load evidence for milestone', milestoneIndex, e);
    }
  };

  const loadDisputeDetails = async () => {
    if (!project) return;
    for (let i = 0; i < project.milestones.length; i++) {
      const m = project.milestones[i];
      if (m.disputeStatus && m.disputeStatus !== 'none') {
        loadEvidenceForMilestone(i);
        try {
          const detail = await api.getDisputeDetail(project.id, i);
          if (detail) {
            setMilestoneDisputeReasons(prev => ({
              ...prev,
              [i]: { reason: detail.reason || '', initiator: detail.initiatorAddress || '' }
            }));
          }
        } catch (e) {
          console.warn('Failed to load dispute detail for milestone', i, e);
        }
      }
    }
  };

  useEffect(() => {
    if (project) {
      loadDisputeDetails();
    }
  }, [project?.id, project?.milestones?.map(m => m.disputeStatus).join(',')]);

  const handleResolveDispute = async (clientPercent: number, providerPercent: number) => {
    if (!project || !account || resolvingMilestone === null) {
      setToast({ message: 'Please connect your wallet', type: 'error' });
      return;
    }

    setResolveModalOpen(false);
    setActionLoading(true);
    setBlockchainLoading(true);
    setBlockchainLoadingMessage('Resolving dispute on blockchain...');
    setError(null);

    try {
      let txHash: string | undefined;

      // On-chain (required if blockchainProjectId exists)
      if (signer && blockchainProjectId != null && blockchainProjectId >= 0) {
        try {
          console.log('Resolving dispute on-chain:', { blockchainProjectId, resolvingMilestone, clientPercent, providerPercent });
          const receipt = await resolveDisputeContract(blockchainProjectId, resolvingMilestone, clientPercent, providerPercent);
          txHash = receipt.hash;
          console.log('On-chain dispute resolution successful, txHash:', txHash);
        } catch (e: any) {
          console.error('On-chain dispute resolution failed:', e);
          const errorMsg = e.message || e.reason || 'Blockchain transaction failed';
          throw new Error(`Blockchain dispute resolution failed: ${errorMsg}. Please try again.`);
        }
      } else if (blockchainProjectId == null) {
        throw new Error('No blockchain project ID found. Cannot resolve dispute.');
      }

      if (!txHash) {
        throw new Error('Transaction hash not received from blockchain');
      }

      setBlockchainLoadingMessage('Recording resolution data...');
      // Off-chain
      await resolveDisputeAPI(project.id, resolvingMilestone, clientPercent, providerPercent, txHash);

      // Add transaction to history
      if (txHash && account) {
        const milestone = project.milestones[resolvingMilestone];
        setTransactions((prev) => {
          // Check if transaction already exists
          const exists = prev.some((tx) => tx.txHash === txHash);
          if (exists) return prev;

          return [
            {
              id: `resolve-${Date.now()}`,
              type: 'resolve',
              txHash,
              milestoneIndex: resolvingMilestone,
              milestoneDescription: milestone.description,
              amount: milestone.amount,
              from: account,
              timestamp: new Date().toISOString(),
            },
            ...prev,
          ];
        });
      }

      setToast({
        message: `Dispute resolved successfully. Funds distributed: ${clientPercent}% to client, ${providerPercent}% to provider.`,
        type: 'success'
      });
      setResolveModalOpen(false);
      setResolvingMilestone(null);
      setBlockchainLoading(false);
      setBlockchainLoadingMessage('');
      setActionLoading(false);
      reloadProject();
    } catch (e: any) {
      const errorMsg = e.message || 'Failed to resolve dispute';
      setError(errorMsg);
      setToast({ message: errorMsg, type: 'error' });
      setBlockchainLoading(false);
      setBlockchainLoadingMessage('');
      setActionLoading(false);
    }
  };

  const handleSubmitEvidence = async (evidenceData: {
    evidenceType: 'text' | 'document';
    evidenceText?: string;
    fileName?: string;
    fileSize?: number;
    fileType?: string;
    fileData?: string;
    submissionNotes?: string;
  }) => {
    if (!project || !account || evidenceMilestone === null) return;

    try {
      await submitEvidenceAPI(project.id, evidenceMilestone, {
        ...evidenceData,
        submittedBy: account,
      });
      setToast({ message: 'Evidence submitted successfully', type: 'success' });
      setEvidenceModalOpen(false);
      setEvidenceMilestone(null);
      await loadEvidenceForMilestone(evidenceMilestone);
    } catch (e: any) {
      setToast({ message: e.message || 'Failed to submit evidence', type: 'error' });
      throw e;
    }
  };

  const handleDeposit = async () => {
    if (!project || !account) {
      setToast({ message: 'Please connect your wallet', type: 'error' });
      return;
    }

    const remainingWei = BigInt(project.totalAmount) - BigInt(project.depositedAmount);
    if (remainingWei <= BigInt(0)) {
      setToast({ message: 'Project is already fully funded', type: 'error' });
      return;
    }

    setActionLoading(true);
    setBlockchainLoading(true);
    setBlockchainLoadingMessage('Depositing funds on blockchain...');
    setError(null);

    try {
      const weiAmount = remainingWei;

      let txHash: string | undefined;

      // On-chain (required if blockchainProjectId exists)
      if (signer && blockchainProjectId != null && blockchainProjectId >= 0) {
        try {
          console.log('Depositing funds on-chain:', { blockchainProjectId, weiAmount: weiAmount.toString() });
          setBlockchainLoadingMessage('Verifying on-chain state...');

          const contract = new ethers.Contract(CONTRACT_ADDRESS, ESCROW_ABI, signer);

          // Pre-flight checks: read on-chain state to diagnose revert before sending tx
          try {
            const onChainProject = await contract.projects(blockchainProjectId);
            const onChainClient = onChainProject[0];
            const onChainDeposited = BigInt(onChainProject[3]);
            const onChainTotal = BigInt(onChainProject[2]);
            const onChainStatus = Number(onChainProject[4]);
            const signerAddress = await signer.getAddress();

            console.log('Pre-flight check:', {
              onChainClient,
              signerAddress,
              onChainStatus,
              onChainDeposited: onChainDeposited.toString(),
              onChainTotal: onChainTotal.toString(),
              weiAmount: weiAmount.toString(),
            });

            if (onChainClient === '0x0000000000000000000000000000000000000000') {
              throw new Error('Project does not exist on blockchain. Check blockchainProjectId.');
            }
            if (onChainStatus !== 0) {
              throw new Error(`Project is not active on-chain (status=${onChainStatus}). Cannot deposit.`);
            }
            if (signerAddress.toLowerCase() !== onChainClient.toLowerCase()) {
              throw new Error(`Only the project client (${onChainClient}) can deposit. Your wallet: ${signerAddress}`);
            }
            if (weiAmount <= BigInt(0)) {
              throw new Error('Deposit amount must be greater than zero.');
            }
            if (onChainDeposited + weiAmount > onChainTotal) {
              throw new Error(`Deposit would exceed total. On-chain deposited: ${onChainDeposited}, total: ${onChainTotal}, trying to deposit: ${weiAmount}`);
            }
          } catch (preflight: any) {
            if (preflight.message && !preflight.message.includes('missing revert')) {
              throw preflight; // Re-throw our diagnostic errors
            }
            console.warn('Pre-flight check failed (non-diagnostic):', preflight);
          }

          setBlockchainLoadingMessage('Waiting for wallet confirmation...');
          const tx = await contract.depositFunds(blockchainProjectId, { value: weiAmount });
          txHash = tx.hash;
          console.log('Transaction sent, txHash:', txHash);

          setBlockchainLoadingMessage('Transaction sent! Waiting for blockchain confirmation...');
          const depositReceipt = await Promise.race([
            tx.wait(1, 90_000),
            new Promise<null>((_, reject) =>
              setTimeout(() => reject(new Error('Transaction confirmation timed out. Check your wallet - it may still be processing.')), 95_000)
            ),
          ]);
          if (!depositReceipt) {
            throw new Error('Transaction was dropped or replaced.');
          }
          console.log('Transaction confirmed');
        } catch (e: any) {
          console.error('On-chain deposit failed:', e);
          const errorMsg = e.message || e.reason || 'Blockchain transaction failed';
          throw new Error(`Blockchain deposit failed: ${errorMsg}`);
        }
      } else if (blockchainProjectId == null) {
        console.warn('No blockchainProjectId found, skipping on-chain deposit');
      }

      // Write deposit transaction directly to DB (no blockchain scanning needed)
      setBlockchainLoadingMessage('Saving to database...');
      try {
        // Update deposited amount
        await api.depositFunds(project.id, weiAmount.toString(), txHash);

        // Save the transaction record
        if (txHash) {
          const depositTx = {
            txHash,
            type: 'deposit',
            amount: weiAmount.toString(),
            from: account,
            to: CONTRACT_ADDRESS,
            blockchainProjectId: blockchainProjectId,
            timestamp: new Date().toISOString(),
          };
          const syncResult = await api.syncTransactions(project.id, [depositTx]);
          if (syncResult && syncResult.length > 0) {
            const sorted = [...syncResult].sort((a: any, b: any) => {
              if (a.type === 'create' && b.type !== 'create') return 1;
              if (b.type === 'create' && a.type !== 'create') return -1;
              return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
            });
            setTransactions(sorted);
          }
        }
      } catch (syncErr: any) {
        console.warn('Failed to save deposit to DB:', syncErr.message);
      }

      setToast({ message: 'Deposit successful! Please upload a document to continue.', type: 'success' });
      setBlockchainLoading(false);
      setBlockchainLoadingMessage('');
      setActionLoading(false);
      reloadProject();
      loadProjectDocuments();
    } catch (e: any) {
      setError(e.message || 'Failed to deposit funds');
      setToast({ message: e.message || 'Failed to deposit funds', type: 'error' });
      setBlockchainLoading(false);
      setBlockchainLoadingMessage('');
      setActionLoading(false);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <DashboardHeader />
        <div className="w-full max-w-[1200px] mx-auto px-4 py-16">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-100 rounded w-1/3" />
            <div className="h-4 bg-gray-100 rounded w-1/2" />
            <div className="h-32 bg-gray-100 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-white">
        <DashboardHeader />
        <div className="w-full max-w-[1200px] mx-auto px-4 py-16 text-center">
          <p className="text-lg text-[#471b007f] font-dm mb-4">Project not found</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-gradient-to-r from-[#FF3333] to-[#FF5555] text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
          >
            Back to My Projects
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <DashboardHeader />

      <div className="w-full max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumbs */}
        <button
          onClick={() => navigate('/dashboard')}
          className="text-sm text-[#471b007f] font-dm hover:text-[#471b00] mb-4"
        >
          Dashboard / My Projects /{' '}
          <span className="text-[#471b00]">{project.title}</span>
        </button>

        {/* Deposit funds - moved to top */}
        {isClient && remaining > 0 && project.status !== 'completed' && (
          <div className="mb-6">
            <div className="bg-white border border-[#471b001e] rounded-2xl p-5">
              <h3 className="text-lg font-semibold text-[#471b00] font-poly mb-4">
                Deposit Funds
              </h3>
              <div className="space-y-4">
                <p className="text-sm text-[#471b007f] font-dm">
                  {hasDeposit
                    ? `Partial deposit detected. Remaining amount: ${remaining.toFixed(4)} MATIC`
                    : `Full contract amount: ${remaining.toFixed(4)} MATIC`}
                </p>

                <button
                  onClick={handleDeposit}
                  disabled={actionLoading}
                  className="px-6 py-2 bg-gradient-to-r from-[#FF3333] to-[#FF5555] text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading ? 'Processing...' : `Deposit ${remaining.toFixed(4)} MATIC`}
                </button>
              </div>
            </div>
          </div>
        )}

        {isClient && remaining <= 0 && hasDeposit && project.status !== 'completed' && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800 font-dm font-semibold">
              Fully funded
            </p>
          </div>
        )}

        {/* Warning messages */}
        {hasDeposit && !hasDocument && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800 font-dm">
              <strong>Deposit successful!</strong> Please upload a document to continue. All actions are blocked until a document is uploaded.
            </p>
          </div>
        )}

        {!hasDeposit && project.status !== 'completed' && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800 font-dm">
              Please make a deposit to activate the project. All actions are blocked until a deposit is made.
            </p>
          </div>
        )}

        {/* Upload Document Section - moved after deposit message */}
        {hasDeposit && (isClient || isProvider) && (
          <div className="mb-6">
            <div className="bg-white border border-[#471b001e] rounded-2xl p-6">
              <h3 className="text-xl font-semibold text-[#471b00] font-poly mb-4">
                Upload Document
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#471b00] font-dm mb-2">
                    Document File {!hasDocument && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="file"
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        setDepositDocument(e.target.files[0]);
                      }
                    }}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    className="block w-full text-sm text-[#471b007f] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#FF3333] file:text-white hover:file:bg-[#FF5555] file:cursor-pointer"
                  />
                  {depositDocument && (
                    <p className="text-xs text-[#471b007f] mt-2">
                      Selected: {depositDocument.name} ({(depositDocument.size / 1024).toFixed(2)} KB)
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#471b00] font-dm mb-2">
                    Comment
                  </label>
                  <textarea
                    value={depositComment}
                    onChange={(e) => setDepositComment(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 border border-[#471b001e] rounded-lg focus:outline-none focus:border-[#FF3333]"
                    placeholder="Add a comment about this document..."
                  />
                </div>
                <button
                  onClick={async () => {
                    if (!depositDocument) {
                      setToast({ message: 'Please select a document', type: 'error' });
                      return;
                    }
                    setActionLoading(true);
                    try {
                      const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
                      if (depositDocument.size > MAX_FILE_SIZE) {
                        throw new Error(`File size exceeds limit of ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
                      }
                      const reader = new FileReader();
                      reader.readAsDataURL(depositDocument);
                      reader.onloadend = async () => {
                        try {
                          const base64Data = (reader.result as string).split(',')[1];
                          await api.uploadDocument({
                            projectId: project.id,
                            milestoneIndex: undefined,
                            fileName: depositDocument.name,
                            fileSize: depositDocument.size,
                            fileType: depositDocument.type,
                            fileData: base64Data,
                            comment: depositComment,
                            uploadedBy: account || '',
                          });
                          setToast({ message: 'Document uploaded successfully! All actions are now enabled.', type: 'success' });
                          setDepositDocument(null);
                          setDepositComment('');
                          await loadProjectDocuments();
                        } catch (e: any) {
                          setToast({ message: e.message || 'Failed to upload document', type: 'error' });
                        } finally {
                          setActionLoading(false);
                        }
                      };
                    } catch (e: any) {
                      setToast({ message: e.message || 'Failed to upload document', type: 'error' });
                      setActionLoading(false);
                    }
                  }}
                  disabled={actionLoading || !depositDocument}
                  className="px-6 py-2 bg-gradient-to-r from-[#FF3333] to-[#FF5555] text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading ? 'Uploading...' : 'Upload Document'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header + Summary */}
        <div className="flex flex-col lg:flex-row gap-6 mb-8">
          {/* Left: Project header */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-semibold text-[#471b00] font-poly">
                {project.title}
              </h1>
              {(isClient || isProvider || isArbitrator) && (
                <span
                  className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    isClient
                      ? 'bg-blue-100 text-blue-700'
                      : isProvider
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-indigo-100 text-indigo-700'
                  }`}
                >
                  {isClient ? 'Client' : isProvider ? 'Provider' : 'Arbitrator'}
                </span>
              )}
            </div>
            {project.description && (
              <p className="text-sm text-[#471b007f] font-dm mb-4">
                {project.description}
              </p>
            )}
            {/* Status badge */}
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                project.status === 'active'
                  ? 'bg-green-50 text-green-700'
                  : project.status === 'completed'
                  ? 'bg-blue-50 text-blue-700'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {project.status.toUpperCase()}
            </span>
          </div>

          {/* Right: Summary card */}
          <div className="w-full lg:w-[320px] bg-white border border-[#471b001e] rounded-2xl p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-[#471b00] font-dm mb-3">
              Project Summary
            </h3>
            <div className="space-y-2 text-sm font-dm text-[#471b007f]">
              <div className="flex justify-between">
                <span>Total</span>
                <span className="font-semibold text-[#471b00]">
                  {total.toFixed(4)} MATIC
                </span>
              </div>
              <div className="flex justify-between">
                <span>Deposited</span>
                <span className="font-semibold text-[#471b00]">
                  {deposited.toFixed(4)} MATIC
                </span>
              </div>
              <div className="flex justify-between">
                <span>Released</span>
                <span className="font-semibold text-[#471b00]">
                  {releasedTotal.toFixed(4)} MATIC
                </span>
              </div>
              <div className="flex justify-between">
                <span>Remaining</span>
                <span className="font-semibold text-[#471b00]">
                  {remaining.toFixed(4)} MATIC
                </span>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mt-4">
              <div className="flex justify-between text-xs text-[#471b007f] mb-1">
                <span>Progress</span>
                <span>{progress}%</span>
              </div>
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-2 bg-gradient-to-r from-[#FF3333] to-[#FF5555]"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Addresses + contract info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {/* Participants */}
          <div className="bg-white border border-[#471b001e] rounded-2xl p-4">
            <h3 className="text-sm font-semibold text-[#471b00] font-dm mb-3">
              Participants
            </h3>
            <div className="space-y-2 text-sm font-dm">
              <div>
                <div className="text-[#471b007f]">Client</div>
                <div className="font-mono text-[#471b00]">
                  {formatAddress(project.client)}
                </div>
              </div>
              <div>
                <div className="text-[#471b007f]">Provider</div>
                <div className="font-mono text-[#471b00]">
                  {formatAddress(project.provider)}
                </div>
              </div>
            </div>
          </div>

          {/* Contract info */}
          <div className="bg-white border border-[#471b001e] rounded-2xl p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-semibold text-[#471b00] font-dm">
                Contract
              </h3>
            </div>
            <div className="space-y-2 text-sm font-dm">
              <div>
                <div className="text-[#471b007f]">Escrow Contract</div>
                <div className="font-mono text-[#471b00] break-all">
                  {project.contractAddress || 'On-chain contract not linked'}
                </div>
              </div>
              <div>
                <div className="text-[#471b007f]">Project ID (on-chain)</div>
                <div className="font-mono text-[#471b00]">
                  {blockchainProjectId != null && blockchainProjectId > 0
                    ? blockchainProjectId
                    : 'N/A'}
                </div>
              </div>
              {(project as any).lastDepositTxHash && (
                <div className="pt-2 border-t border-[#471b001e]">
                  <div className="text-[#471b007f] mb-1">Last Deposit</div>
                  <a
                    href={getTransactionUrl((project as any).lastDepositTxHash)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-[#471b00] text-xs break-all hover:text-[#FF3333] transition-colors flex items-center gap-1"
                  >
                    <span>{(project as any).lastDepositTxHash.slice(0, 10)}...{(project as any).lastDepositTxHash.slice(-8)}</span>
                    <span>🔗</span>
                  </a>
                  {(project as any).lastDepositDate && (
                    <div className="text-xs text-[#471b007f] mt-1">
                      {new Date((project as any).lastDepositDate).toLocaleString()}
                    </div>
                  )}
                </div>
              )}
              {project.contractAddress && (
                <div className="pt-2 border-t border-[#471b001e]">
                  <a
                    href={getContractUrl(project.contractAddress)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#471b007f] hover:text-[#FF3333] transition-colors flex items-center gap-1"
                  >
                    <span>View Contract on Explorer</span>
                    <span>🔗</span>
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Arbitrator Section */}
        <div className="mb-8">
          <div className="bg-white border border-[#471b001e] rounded-2xl p-6">
            <h3 className="text-xl font-semibold text-[#471b00] font-poly mb-4">
              Dispute Arbitrator
            </h3>

            {/* Display assigned arbitrator */}
            {(project as any).arbitratorAddress ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm font-medium text-green-700 font-dm">
                    Arbitrator Assigned
                  </span>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-[#471b007f] font-dm mb-1">Arbitrator Address</p>
                      <p className="text-sm font-mono font-medium text-[#471b00]">
                        {formatAddress((project as any).arbitratorAddress)}
                      </p>
                    </div>
                    <a
                      href={`https://amoy.polygonscan.com/address/${(project as any).arbitratorAddress}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#FF3333] hover:text-[#FF5555] text-sm font-medium"
                    >
                      View →
                    </a>
                  </div>
                  <p className="text-xs text-[#471b007f] font-dm mt-3">
                    This address has authority to resolve disputes on this project.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Show assignment form only to client */}
                {isClient ? (
                  <div className="space-y-4">
                    <p className="text-sm text-[#471b007f] font-dm">
                      Assign a trusted third party to resolve disputes. This can only be set once.
                    </p>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex gap-2">
                        <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="text-sm text-blue-800 font-dm">
                          <p className="font-medium mb-1">Important:</p>
                          <ul className="list-disc list-inside space-y-1 text-xs">
                            <li>Choose a neutral party both you and the provider trust</li>
                            <li>The arbitrator can resolve disputes and distribute funds</li>
                            <li>This decision cannot be changed once set</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="arbitrator" className="block text-sm font-medium text-[#471b00] mb-2 font-dm">
                        Arbitrator Wallet Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="arbitrator"
                        value={arbitratorAddress}
                        onChange={(e) => setArbitratorAddress(e.target.value)}
                        disabled={actionLoading}
                        className="w-full px-4 py-2 border border-[#471b001e] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF3333] focus:border-transparent font-mono text-sm disabled:opacity-50"
                        placeholder="0x..."
                      />
                    </div>
                    <button
                      onClick={handleSetArbitrator}
                      disabled={actionLoading || !arbitratorAddress}
                      className="px-6 py-2 bg-gradient-to-r from-[#FF3333] to-[#FF5555] text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      {actionLoading ? 'Assigning...' : 'Assign Arbitrator'}
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-[#471b007f] font-dm">
                      {isProvider ? 'Waiting for client to assign an arbitrator' : 'Only the client can assign an arbitrator'}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Milestones */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-[#471b00] font-poly mb-4">
            Milestones
          </h2>
          <div className="space-y-4">
            {project.milestones.map((milestone, index) => (
              <MilestoneCard
                key={milestone.id}
                milestone={milestone}
                milestoneId={index}
                isClient={!!isClient}
                isProvider={!!isProvider}
                isArbitrator={!!isArbitrator}
                currentUser={account || ''}
                onApprove={() => handleApprove(index)}
                onRelease={() => handleRelease(index)}
                onOpenDispute={() => handleOpenDisputeModal(index)}
                onResolveDispute={() => handleOpenResolveModal(index)}
                onSubmitEvidence={() => handleOpenEvidenceModal(index)}
                loading={actionLoading}
                depositRequired={!hasDeposit}
                documentRequired={!hasDocument}
                disputeReason={milestoneDisputeReasons[index]?.reason}
                disputeInitiator={milestoneDisputeReasons[index]?.initiator}
                evidence={milestoneEvidence[index] || []}
                clientAddress={project.client}
                providerAddress={project.provider}
                arbitratorAddress={(project as any).arbitratorAddress}
                documents={milestoneDocuments[index] || []}
                onUploadDocument={async (milestoneIndex: number, file: File, comment: string) => {
                  if (!project || !account) return;
                  setUploadingMilestone(milestoneIndex);
                  try {
                    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
                    if (file.size > MAX_FILE_SIZE) {
                      throw new Error(`File size exceeds limit of ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
                    }
                    const reader = new FileReader();
                    reader.readAsDataURL(file);
                    reader.onloadend = async () => {
                      try {
                        const base64Data = (reader.result as string).split(',')[1];
                        await api.uploadDocument({
                          projectId: project.id,
                          milestoneIndex: milestoneIndex,
                          fileName: file.name,
                          fileSize: file.size,
                          fileType: file.type,
                          fileData: base64Data,
                          comment: comment,
                          uploadedBy: account,
                        });
                        setToast({ message: 'Document uploaded successfully!', type: 'success' });
                        await loadProjectDocuments();
                      } catch (e: any) {
                        setToast({ message: e.message || 'Failed to upload document', type: 'error' });
                      } finally {
                        setUploadingMilestone(null);
                      }
                    };
                  } catch (e: any) {
                    setToast({ message: e.message || 'Failed to upload document', type: 'error' });
                    setUploadingMilestone(null);
                  }
                }}
                uploadingDocument={uploadingMilestone === index}
              />
            ))}
          </div>
        </div>

        {/* Project Documents - visible to client, provider, and arbitrator */}
        {(isClient || isProvider || isArbitrator) && (
          <div className="mb-8">
            <div className="bg-white border border-[#471b001e] rounded-2xl p-6">
              <h3 className="text-xl font-semibold text-[#471b00] font-poly mb-4">
                Project Documents
              </h3>
              {documentsLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3333]"></div>
                </div>
              ) : projectDocuments.length === 0 ? (
                <p className="text-sm text-[#471b007f] font-dm text-center py-8">
                  No documents uploaded yet
                </p>
              ) : (
                <div className="space-y-3">
                  {projectDocuments.map((doc: any) => {
                    const isImage = doc.file_type?.startsWith('image/');
                    const documentUrl = `${import.meta.env.VITE_API_URL || (import.meta.env.PROD ? "" : "http://localhost:3001")}/api/get-document?documentId=${doc.id}`;

                    return (
                      <div
                        key={doc.id}
                        className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:bg-gray-100 transition-colors"
                      >
                        {isImage && (
                          <div className="mb-3">
                            <img
                              src={documentUrl}
                              alt={doc.file_name}
                              className="w-full max-w-md rounded-lg border border-gray-300"
                              onError={(e) => {
                                // Hide image if it fails to load
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-lg">{isImage ? '🖼️' : '📄'}</span>
                              <a
                                href={documentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium text-[#471b00] hover:text-[#FF3333] transition-colors truncate"
                              >
                                {doc.file_name}
                              </a>
                            </div>
                            {doc.comment && (
                              <p className="text-xs text-[#471b007f] mt-1 truncate">{doc.comment}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                              {(doc.file_size / 1024).toFixed(2)} KB | Uploaded by{' '}
                              {doc.uploaded_by?.slice(0, 6)}...{doc.uploaded_by?.slice(-4)} on{' '}
                              {new Date(doc.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <a
                            href={documentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-4 px-3 py-1.5 bg-gradient-to-r from-[#FF3333] to-[#FF5555] text-white rounded-lg font-medium hover:opacity-90 transition-opacity text-sm"
                          >
                            {isImage ? 'View Full' : 'Download'}
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Transaction History */}
        <div className="mb-8">
          <TransactionHistory
            transactions={transactions}
            projectId={project.id}
            blockchainProjectId={blockchainProjectId}
            clientAddress={project.client}
            providerAddress={project.provider}
          />
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {blockchainLoading && (
        <BlockchainLoader message={blockchainLoadingMessage} />
      )}

      <DisputeModal
        isOpen={disputeModalOpen}
        onClose={() => {
          setDisputeModalOpen(false);
          setDisputingMilestone(null);
        }}
        onSubmit={handleOpenDispute}
        milestoneId={disputingMilestone ?? 0}
        milestoneDescription={
          disputingMilestone !== null && project?.milestones[disputingMilestone]
            ? project.milestones[disputingMilestone].description
            : ''
        }
        loading={actionLoading}
      />

      <DisputeResolutionModal
        isOpen={resolveModalOpen}
        onClose={() => {
          setResolveModalOpen(false);
          setResolvingMilestone(null);
        }}
        onSubmit={handleResolveDispute}
        milestoneId={resolvingMilestone ?? 0}
        milestoneDescription={
          resolvingMilestone !== null && project?.milestones[resolvingMilestone]
            ? project.milestones[resolvingMilestone].description
            : ''
        }
        milestoneAmount={
          resolvingMilestone !== null && project?.milestones[resolvingMilestone]
            ? project.milestones[resolvingMilestone].amount
            : '0'
        }
        loading={actionLoading}
      />

      <EvidenceSubmissionModal
        isOpen={evidenceModalOpen}
        onClose={() => {
          setEvidenceModalOpen(false);
          setEvidenceMilestone(null);
        }}
        onSubmit={handleSubmitEvidence}
        milestoneId={evidenceMilestone ?? 0}
        milestoneDescription={
          evidenceMilestone !== null && project?.milestones[evidenceMilestone]
            ? project.milestones[evidenceMilestone].description
            : ''
        }
        loading={actionLoading}
      />
    </div>
  );
};

export default ProjectDetailPage;


